import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Phone, Mic, Video, Hourglass, LogOut, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import InsufficientBalanceDialog from '../InsufficientBalanceDialog';
import { langFlag } from '../../lib/creatorMeta';
import { CallAvailabilityButton } from './CallAvailabilityButton';
import {
  getCreatorCallStatus, initiateCall, joinWaitlist, leaveWaitlist,
} from '../../api/o2oCalls';

const StateDot = ({ state }) => {
  const { t } = useTranslation('o2o');
  const map = {
    available: ['bg-emerald-500', t('call_availability.available')],
    busy: ['bg-red-500', t('call_availability.busy')],
    offline: ['bg-neutral-400', t('call_availability.offline')],
  };
  const [color, label] = map[state] || map.offline;
  return (
    <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground" data-testid="o2o-creator-state">
      <span className={`h-2 w-2 rounded-full ${color} ${state === 'available' ? 'animate-pulse' : ''}`} />
      {label}
    </span>
  );
};

/**
 * Phase 54 · "📞 Chiama il creator" — profile CTA for instant
 * pay-per-minute calls. Shown UNDER the Follow/Message row.
 */
const CallCreatorSection = ({ creatorId, username, unavailableFallback = null, onStatusChange }) => {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [modeOpen, setModeOpen] = useState(false);
  const [waitOpen, setWaitOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [insufficient, setInsufficient] = useState(null);
  const [myEntry, setMyEntry] = useState(null);
  const timer = useRef(null);
  const { t } = useTranslation('o2o');
  const activeCreator = useRef(creatorId);

  const load = async () => {
    try {
      const s = await getCreatorCallStatus(creatorId);
      if (activeCreator.current !== creatorId) return;
      setStatus(s);
      if (!s?.waitlist?.my_entry_id) setMyEntry(null);
    } catch (_e) { if (activeCreator.current === creatorId) setStatus(null); }
  };

  useEffect(() => {
    activeCreator.current = creatorId;
    setStatus(null);
    if (!me || !creatorId) return undefined;
    load();
    const onVisible = () => { if (!document.hidden) load(); };
    timer.current = setInterval(onVisible, 10000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('nymia:o2o-state-changed', onVisible);
    return () => { activeCreator.current = null; clearInterval(timer.current); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); window.removeEventListener('nymia:o2o-state-changed', onVisible); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId, me]);

  useEffect(() => { onStatusChange?.(status); }, [status, onStatusChange]);

  if (!me || !status) return null;
  // Phase F5 — optional presentational fallback for the Vetrina section
  // (default null = identical behavior for all existing usages).
  if (!status.enabled) return unavailableFallback;

  const bothModes = status.audio_enabled && status.video_enabled;
  const singleMode = status.audio_enabled ? 'audio' : 'video';

  const startCall = async (mode) => {
    if (busy) return;
    setBusy(true);
    setModeOpen(false);
    try {
      const call = await initiateCall(creatorId, mode);
      navigate(`/call/${call.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 402) {
        setInsufficient({ required: status.min_credits, balance: me.wallet_balance || 0 });
      } else {
        toast({
          title: 'Chiamata non avviata',
          description: String(detail).split('|').pop() || 'Riprova più tardi.',
          variant: 'destructive',
        });
        load();
      }
    } finally {
      setBusy(false);
    }
  };

  const onCallClick = () => {
    if (bothModes) setModeOpen(true);
    else startCall(singleMode);
  };

  const onJoinWaitlist = async (mode) => {
    if (busy) return;
    setBusy(true);
    try {
      const entry = await joinWaitlist(creatorId, mode);
      setMyEntry(entry);
      toast({
        title: 'Sei in lista d\'attesa',
        description: `Posizione ${entry.position} · attesa stimata ~${entry.estimated_wait_minutes} min. I crediti sono bloccati, non addebitati.`,
      });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 402) {
        setInsufficient({ required: status.min_credits, balance: me.wallet_balance || 0 });
      } else {
        toast({ title: 'Impossibile entrare in lista', description: String(detail).split('|').pop(), variant: 'destructive' });
      }
    } finally {
      setBusy(false);
      setWaitOpen(false);
    }
  };

  const onLeaveWaitlist = async () => {
    const id = myEntry?.id || status?.waitlist?.my_entry_id;
    if (!id || busy) return;
    setBusy(true);
    try {
      await leaveWaitlist(id);
      setMyEntry(null);
      toast({ title: 'Hai lasciato la lista d\'attesa', description: 'I crediti bloccati sono stati sbloccati.' });
      load();
    } catch (_e) { /* ignore */ } finally { setBusy(false); }
  };

  const inWaitlist = !!(myEntry || status?.waitlist?.my_entry_id);

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="call-creator-section">
      <StateDot state={status.accepting_calls ? status.state : 'offline'} />

      {status.can_call && (
        <CallAvailabilityButton
          available
          onClick={onCallClick}
          disabled={busy}
          className="h-8 rounded-md px-4 text-sm"
          testId="call-creator-btn"
        >
          <span className="ml-1.5 text-[10px] font-medium opacity-90">· {status.rate_credits_per_min} cr/min</span>
        </CallAvailabilityButton>
      )}

      {status.accepting_calls && status.state === 'busy' && !inWaitlist && status.waitlist?.enabled && creatorId !== me.id && (
        <Button
          onClick={() => setWaitOpen(true)}
          disabled={busy || status.waitlist.is_full}
          variant="secondary"
          className="h-8 rounded-md px-4 text-sm font-semibold"
          data-testid="join-waitlist-btn"
        >
          <Hourglass className="mr-1.5 h-3.5 w-3.5" />
          {status.waitlist.is_full ? 'Lista d\'attesa piena' : `Lista d'attesa · ~${status.waitlist.estimated_wait_minutes} min`}
        </Button>
      )}

      {status.accepting_calls && status.state === 'busy' && !status.waitlist?.enabled && !inWaitlist && (
        <span className="text-xs text-muted-foreground" data-testid="creator-busy-note">
          Riprova tra ~{status.waitlist?.estimated_wait_minutes || 5} min
        </span>
      )}

      {inWaitlist && (
        <div className="flex items-center gap-2" data-testid="waitlist-status">
          <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold gold-text">
            In attesa{myEntry?.position ? ` · pos. ${myEntry.position}` : ''} — ti chiameremo appena libero
          </span>
          <Button variant="ghost" size="sm" onClick={onLeaveWaitlist} className="h-7 px-2 text-xs text-red-500" data-testid="leave-waitlist-btn">
            <LogOut className="mr-1 h-3 w-3" /> Esci
          </Button>
        </div>
      )}

      {!status.accepting_calls && (
        <span className="text-xs text-muted-foreground" data-testid="creator-offline-note">
          {t('call_availability.unavailable_note')}
        </span>
      )}

      {/* Mode picker */}
      <Dialog open={modeOpen} onOpenChange={setModeOpen}>
        <DialogContent className="max-w-sm" data-testid="call-mode-dialog">
          <DialogHeader>
            <DialogTitle>Chiama @{username}</DialogTitle>
            <DialogDescription>
              {status.rate_credits_per_min} crediti al minuto · max {status.max_duration_minutes} min.
              Paghi solo i minuti effettivi: i crediti extra bloccati vengono sbloccati subito a fine chiamata.
            </DialogDescription>
          </DialogHeader>
          {(status.topics?.length > 0 || status.languages?.length > 0) && (
            <div className="flex flex-wrap gap-1.5" data-testid="call-dialog-creator-info">
              {(status.topics || []).map((topic) => (
                <span key={topic} className="rounded-full border border-amber-500/30 bg-amber-500/5 px-2 py-0.5 text-[10px] font-semibold gold-text">{topic}</span>
              ))}
              {status.languages?.length > 0 && (
                <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold">
                  {status.languages.map(langFlag).join(' ')}
                </span>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 py-2">
            <Button onClick={() => startCall('audio')} variant="secondary" className="h-16 flex-col gap-1" data-testid="call-mode-audio">
              <Mic className="h-5 w-5" /> Audio
            </Button>
            <Button onClick={() => startCall('video')} className="h-16 flex-col gap-1 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="call-mode-video">
              <Video className="h-5 w-5" /> Video
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Waitlist confirm */}
      <Dialog open={waitOpen} onOpenChange={setWaitOpen}>
        <DialogContent className="max-w-sm" data-testid="waitlist-dialog">
          <DialogHeader>
            <DialogTitle>Lista d'attesa · @{username}</DialogTitle>
            <DialogDescription>
              {status.waitlist?.count || 0} in coda · attesa stimata ~{status.waitlist?.estimated_wait_minutes} min.
              I crediti vengono bloccati (non addebitati) e sbloccati se esci dalla lista.
              Quando la Voce Pro si libera, ti chiameremo automaticamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWaitOpen(false)} data-testid="waitlist-cancel-btn">Annulla</Button>
            <Button
              onClick={() => onJoinWaitlist(bothModes ? 'video' : singleMode)}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              disabled={busy}
              data-testid="waitlist-confirm-btn"
            >
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Entra in lista
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InsufficientBalanceDialog
        open={!!insufficient}
        onOpenChange={(v) => !v && setInsufficient(null)}
        balance={insufficient?.balance || 0}
        required={insufficient?.required || 0}
        context="call"
      />
    </div>
  );
};

export default CallCreatorSection;
